import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Plus,
  Edit,
  Trash2,
  Image as ImageIcon,
  Eye,
  EyeOff,
  GripVertical,
  X,
} from 'lucide-react';
import api from '../utils/axios';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import FileUpload from '../components/FileUpload';

// ── Banner Modal ─────────────────────────────────────────────────────────────

const BannerModal = ({ banner, onClose }) => {
  const queryClient = useQueryClient();
  const [imageUrl, setImageUrl] = useState(banner?.image_url || '');

  const createMutation = useMutation(
    async (data) => {
      const response = await api.post('/vendor/banners', data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendor-banners']);
        showSuccessToast('Banner created successfully!', 'banner-create');
        onClose();
      },
      onError: (error) => {
        showErrorToast(
          error.response?.data?.message || 'Failed to create banner',
          'banner-create'
        );
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/vendor/banners/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendor-banners']);
        showSuccessToast('Banner updated successfully!', 'banner-update');
        onClose();
      },
      onError: (error) => {
        showErrorToast(
          error.response?.data?.message || 'Failed to update banner',
          'banner-update'
        );
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const url = imageUrl || formData.get('image_url');
    if (!url) {
      showErrorToast('Please upload a banner image', 'banner-validation');
      return;
    }

    const bannerData = {
      title: formData.get('title') || '',
      sort_order: parseInt(formData.get('sort_order')) || 0,
      is_active: formData.get('is_active') === 'true',
      image_url: url,
    };

    if (banner) {
      updateMutation.mutate({ id: banner.id, data: bannerData });
    } else {
      createMutation.mutate(bannerData);
    }
  };

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-secondary-200">
          <h2 className="text-lg font-semibold text-secondary-900">
            {banner ? 'Edit Banner' : 'Add Banner'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-secondary-100 transition-colors"
          >
            <X className="h-5 w-5 text-secondary-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Banner Image *
            </label>
            <FileUpload
              onUploadSuccess={(file) => {
                setImageUrl(file.url);
              }}
              onUploadError={(error) => console.error('Upload error:', error)}
              multiple={false}
              showPreview={true}
              existingImageUrl={banner?.image_url || null}
              placeholder="Upload banner image (recommended: 1200x400)"
            />
            <input type="hidden" name="image_url" value={imageUrl} />
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              Title (optional)
            </label>
            <input
              type="text"
              name="title"
              defaultValue={banner?.title || ''}
              placeholder="e.g. Summer Special Offer"
              className="input-field"
            />
          </div>

          {/* Sort Order */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              Sort Order
            </label>
            <input
              type="number"
              name="sort_order"
              defaultValue={banner?.sort_order ?? 0}
              min="0"
              className="input-field"
            />
            <p className="mt-1 text-xs text-secondary-500">
              Lower number appears first in the carousel
            </p>
          </div>

          {/* Active Toggle */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              Status
            </label>
            <select
              name="is_active"
              defaultValue={banner?.is_active !== false ? 'true' : 'false'}
              className="input-field"
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-3 border-t border-secondary-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary px-4 py-2"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary px-4 py-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {banner ? 'Updating...' : 'Creating...'}
                </span>
              ) : banner ? (
                'Update Banner'
              ) : (
                'Create Banner'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Banners Page ─────────────────────────────────────────────────────────────

const Banners = () => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);

  // Fetch banners
  const {
    data: bannersData,
    isLoading,
    error,
  } = useQuery(
    ['vendor-banners'],
    async () => {
      const response = await api.get('/vendor/banners');
      return response.data;
    },
    {
      retry: 3,
      retryDelay: 1000,
      onError: () => {
        showErrorToast('Failed to load banners', 'banners-load');
      },
    }
  );

  // Toggle active status
  const toggleMutation = useMutation(
    async ({ id, is_active }) => {
      const response = await api.put(`/vendor/banners/${id}`, { is_active });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendor-banners']);
        showSuccessToast('Banner status updated', 'banner-toggle');
      },
      onError: (error) => {
        showErrorToast(
          error.response?.data?.message || 'Failed to update status',
          'banner-toggle'
        );
      },
    }
  );

  // Delete banner
  const deleteMutation = useMutation(
    async (id) => {
      await api.delete(`/vendor/banners/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendor-banners']);
        showSuccessToast('Banner deleted successfully', 'banner-delete');
      },
      onError: (error) => {
        showErrorToast(
          error.response?.data?.message || 'Failed to delete banner',
          'banner-delete'
        );
      },
    }
  );

  const handleDelete = (banner) => {
    if (window.confirm(`Delete banner "${banner.title || 'Untitled'}"? This cannot be undone.`)) {
      deleteMutation.mutate(banner.id);
    }
  };

  const handleToggle = (banner) => {
    toggleMutation.mutate({ id: banner.id, is_active: !banner.is_active });
  };

  const banners = bannersData?.banners || [];

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500 text-lg font-medium">Failed to load banners</p>
          <button
            onClick={() => queryClient.invalidateQueries(['vendor-banners'])}
            className="mt-3 btn-primary px-4 py-2"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Banners</h1>
          <p className="text-sm text-secondary-500 mt-1">
            Manage your app banner images. Active banners appear in the mobile app carousel.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary px-4 py-2 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Banner
        </button>
      </div>

      {/* Banner Count */}
      <div className="flex items-center gap-4">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {banners.length} Total
        </span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          {banners.filter((b) => b.is_active).length} Active
        </span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-600">
          {banners.filter((b) => !b.is_active).length} Inactive
        </span>
      </div>

      {/* Empty State */}
      {banners.length === 0 ? (
        <div className="card text-center py-16">
          <ImageIcon className="mx-auto h-16 w-16 text-secondary-300" />
          <h3 className="mt-4 text-lg font-medium text-secondary-900">No banners yet</h3>
          <p className="mt-2 text-sm text-secondary-500">
            Upload banner images to display in your mobile app carousel.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-6 btn-primary px-6 py-2 inline-flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Your First Banner
          </button>
        </div>
      ) : (
        /* Banner Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {banners.map((banner) => (
            <div
              key={banner.id}
              className={`card-hover overflow-hidden relative ${
                !banner.is_active ? 'opacity-60' : ''
              }`}
            >
              {/* Sort Order Badge */}
              <div className="absolute top-3 left-3 z-10 flex items-center gap-1 bg-black bg-opacity-60 text-white text-xs font-medium px-2 py-1 rounded-full">
                <GripVertical className="h-3 w-3" />
                #{banner.sort_order}
              </div>

              {/* Status Badge */}
              <div
                className={`absolute top-3 right-3 z-10 text-xs font-medium px-2 py-1 rounded-full ${
                  banner.is_active
                    ? 'bg-green-500 text-white'
                    : 'bg-secondary-400 text-white'
                }`}
              >
                {banner.is_active ? 'Active' : 'Inactive'}
              </div>

              {/* Banner Image */}
              <div className="aspect-[3/1] bg-secondary-100">
                <img
                  src={banner.image_url}
                  alt={banner.title || 'Banner'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '';
                    e.target.alt = 'Failed to load image';
                    e.target.className = 'w-full h-full flex items-center justify-center bg-secondary-100';
                  }}
                />
              </div>

              {/* Banner Info */}
              <div className="p-4">
                <h3 className="font-medium text-secondary-900 truncate">
                  {banner.title || 'Untitled Banner'}
                </h3>
                <p className="text-xs text-secondary-500 mt-1">
                  Created {new Date(banner.created_at).toLocaleDateString()}
                </p>

                {/* Actions */}
                <div className="flex items-center gap-2 mt-4">
                  <button
                    onClick={() => handleToggle(banner)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                      banner.is_active
                        ? 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                    title={banner.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {banner.is_active ? (
                      <>
                        <EyeOff className="h-3.5 w-3.5" /> Hide
                      </>
                    ) : (
                      <>
                        <Eye className="h-3.5 w-3.5" /> Show
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => setEditingBanner(banner)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                  >
                    <Edit className="h-3.5 w-3.5" /> Edit
                  </button>

                  <button
                    onClick={() => handleDelete(banner)}
                    className="flex items-center justify-center p-2 rounded-lg text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <BannerModal onClose={() => setShowCreateModal(false)} />
      )}
      {editingBanner && (
        <BannerModal
          banner={editingBanner}
          onClose={() => setEditingBanner(null)}
        />
      )}
    </div>
  );
};

export default Banners;
